import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Text,
  TextInput,
  Stack,
  ScrollArea,
  ActionIcon,
  Group,
  Drawer,
  Button,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconEyeOff,
  IconEye,
  IconSearch,
  IconArrowUp,
  IconArrowDown,
  IconPencil,
  IconPlus,
} from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons';
import { editIdeaPage } from '../../../api/idea';
import {useAuth0} from "@auth0/auth0-react";
import {useUserPermissions} from "../../../hooks/useUserPermissions";
import { AddColumnModal } from './AddColumnModal';

interface ManageColumnsDrawerProps {
  opened: boolean;
  onClose: () => void;
  columns: any[];
  updateColumn: (column: any) => void;
  onSaveColumns: (columns: any[]) => void;
  onEditColumn: (column: any, anchorRect?: DOMRect) => void;
  originalColumns?: any[];
  subPageKey?: string;
  isAiView?: boolean;
  /** After a new column is persisted (add-column API); refetches page columns. */
  onColumnAdded?: () => void;
}

const getColumnTitle = (col: any): string => {
  if (typeof col.title === 'string') return col.title;
  if (col.title?.props?.children) return String(col.title.props.children);
  return col.accessor || '';
}

const cardSx = {
  borderRadius: 8,
  padding: '10px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minHeight: 44,
  backgroundColor: '#fff',
  border: '1px solid #e9ecef',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
};

export const ManageColumnsDrawer: React.FC<ManageColumnsDrawerProps> = ({
  opened,
  onClose,
  columns,
  updateColumn,
  onSaveColumns,
  onEditColumn,
  originalColumns,
  subPageKey,
  isAiView = false,
  onColumnAdded,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 150);
  const [isSaving, setIsSaving] = useState(false);
  const [addColumnModalOpened, setAddColumnModalOpened] = useState(false);

  useEffect(() => {
    if (!opened) {
      setAddColumnModalOpened(false);
    }
  }, [opened]);

  const auth0 = useAuth0();
  const {isAdmin} = useUserPermissions();

  const visibleColumns = useMemo(
    () =>
      columns
        .filter((c: any) => c.enabled !== false && !c.hidden)
        .sort((a: any, b: any) => (a.index ?? 0) - (b.index ?? 0)),
    [columns]
  );

  const hiddenColumns = useMemo(
    () => columns.filter((c: any) => c.hidden),
    [columns]
  );

  const filterBySearch = (list: any[]) => {
    if (!debouncedSearch.trim()) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter(
      (c: any) =>
        getColumnTitle(c).toLowerCase().includes(q) ||
        (c.accessor && String(c.accessor).toLowerCase().includes(q))
    );
  };

  const filteredVisible = filterBySearch(visibleColumns);
  const filteredHidden = filterBySearch(hiddenColumns);

  const handleToggleVisible = (col: any) => {
    updateColumn({ ...col, hidden: true });
  };

  const handleToggleHidden = (col: any) => {
    updateColumn({ ...col, hidden: false });
  };

  const handleMoveColumn = (index: number, isUp = false) => {
    const reordered = [...visibleColumns];

    if (isUp) {
      if (index <= 0) return;

      [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
    } else {
      if (index >= visibleColumns.length - 1) return;

      [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
    }

    reordered.forEach((col, i) => {
      col.index = i;
    });

    const withHidden = columns.map((c: any) => {
      const inVisible = reordered.find((r: any) => r.accessor === c.accessor);
      return inVisible ? { ...inVisible } : c;
    });
    onSaveColumns(withHidden);
  };

  const handleSavePageColumns = async () => {
    setIsSaving(true);
    try {
      const columnsToSave = columns.map((col: any) => {
        return {
          ...(col || {}),
          accessor: col.accessor,
          hidden: col.hidden || false,
          index: col.index ?? 0,
          enabled: col.enabled !== false
        };
      });

      await editIdeaPage(auth0, {
        key: subPageKey || 'all',
        columns: columnsToSave,
      });

      showNotification({
        title: 'Saved successfully!',
        color: 'teal',
        message: 'Page columns have been saved',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 2000,
      });
    } catch (error) {
      console.log('error', error);
      showNotification({
        title: 'Error',
        color: 'red',
        message: 'Failed to save page columns. Please try again.',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Drawer.Root
        opened={opened}
        onClose={onClose}
        position="right"
        transitionProps={{ transition: 'slide-left', duration: 200, timingFunction: 'ease' }}
        styles={{
          content: { backgroundColor: '#fff', display: 'flex', flexDirection: 'column' },
          header: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '10px',
            flexDirection: 'column',
            padding: '20px 40px 16px'
          },
          body: { 
            padding: '0 40px 20px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Drawer.Overlay />
        <Drawer.Content>
          <Drawer.Header>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Drawer.Title>
                <Text fw={700} fz="lg" c="dark.8">
                  Columns
                </Text>
              </Drawer.Title>
              {isAdmin && !isAiView && (
                <Tooltip
                  multiline
                  label='Saves the current column configuration as the default for all users in the workspace'
                >
                  <Button
                    variant="outline"
                    onClick={handleSavePageColumns}
                    loading={isSaving}
                    size="sm"
                    sx={{
                      height: 32,
                      borderColor: "#D8D8DB",
                      color: "black",
                      fontSize: 12,
                      fontWeight: 500,
                      ":hover": {
                        backgroundColor: "#5C5CEB1A"
                      }
                    }}
                  >
                    Save as Default
                  </Button>
                </Tooltip>
              )}
            </Box>
            <TextInput
              placeholder="Search columns"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              icon={<IconSearch size={16} color="#616161" />}
              size="sm"
              styles={{
                root: { width: '100%' },
                input: {
                  borderRadius: 8,
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                  paddingLeft: 14,
                  paddingRight: 14,
                  minHeight: 40,
                  '&::placeholder': {
                    color: '#616161',
                  },
                },
              }}
            />
          </Drawer.Header>
          <Drawer.Body>
            {!isAiView && (
              <Button
                variant="light"
                color="indigo"
                leftIcon={<IconPlus size={16} />}
                onClick={() => setAddColumnModalOpened(true)}
                mb="md"
                fullWidth
                sx={{
                  fontWeight: 500,
                  borderRadius: 8,
                }}
              >
                Add column
              </Button>
            )}
            <ScrollArea style={{ flex: 1 }}>
              <Stack spacing="md">
                <Box>
                  <Text fw={700} fz="sm" c="dark.7" mb="xs">
                  Visible
                  </Text>
                  <Stack spacing={8}>
                    {filteredVisible.map((col: any) => {
                      const globalIndex = visibleColumns.findIndex((c: any) => c.accessor === col.accessor);
                      return (
                        <Box
                          key={col.accessor}
                          sx={{
                            ...cardSx,
                            '&:hover': {
                              borderColor: '#dee2e6',
                              backgroundColor: '#f8f9fa',
                            },
                            '&:hover .column-actions': {
                              opacity: 1,
                            },
                          }}
                          className="column-card-visible"
                        >
                          <Group
                            spacing={10}
                            noWrap
                            style={{ flex: 1, minWidth: 0 }}
                            aria-label={`Hide ${getColumnTitle(col)}`}
                          >
                            <IconEye
                              size={18}
                              color="#495057"
                              stroke={2}
                              style={{ flexShrink: 0, cursor: 'pointer' }}
                              onClick={() => handleToggleVisible(col)}
                            />
                            <Text size="sm" c="dark.7" fw={400} truncate>
                              {getColumnTitle(col)}
                            </Text>
                          </Group>
                          <Group spacing={0} className="column-actions" sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}>
                            <ActionIcon
                              variant="subtle"
                              color="dark"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveColumn(globalIndex, true);
                              }}
                              disabled={globalIndex <= 0}
                              aria-label="Move up"
                            >
                              <IconArrowUp size={14} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="dark"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveColumn(globalIndex);
                              }}
                              disabled={globalIndex >= visibleColumns.length - 1}
                              aria-label="Move down"
                            >
                              <IconArrowDown size={14} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="dark"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                const rowEl = (e.currentTarget as HTMLElement).closest('.column-card-visible') as HTMLElement | null;
                                onEditColumn(col, rowEl?.getBoundingClientRect());
                              }}
                              aria-label="Edit column"
                            >
                              <IconPencil size={14} />
                            </ActionIcon>
                          </Group>
                        </Box>
                      );
                    })}
                    {filteredVisible.length === 0 && (
                      <Text size="sm" c="dimmed">
                        {debouncedSearch.trim() ? 'No visible columns match search' : 'No visible columns'}
                      </Text>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Text fw={700} fz="sm" c="dark.7" mb="xs">
                  Hidden
                  </Text>
                  <Stack spacing={8}>
                    {filteredHidden.map((col: any) => (
                      <Box
                        key={col.accessor}
                        sx={{
                          ...cardSx,
                          '&:hover': {
                            borderColor: '#dee2e6',
                            backgroundColor: '#f8f9fa',
                          },
                        }}
                        className="column-card-hidden"
                      >
                        <Group spacing={10} noWrap>
                          <IconEyeOff
                            onClick={() => handleToggleHidden(col)}
                            size={18}
                            color="#adb5bd"
                            stroke={1.25}
                            style={{ flexShrink: 0, opacity: 0.85, cursor: 'pointer' }}
                          />
                          <Text size="sm" c="gray.5" fw={400}>
                            {getColumnTitle(col)}
                          </Text>
                        </Group>
                      </Box>
                    ))}
                    {filteredHidden.length === 0 && (
                      <Text size="sm" c="dimmed">
                        {debouncedSearch.trim() ? 'No hidden columns match search' : 'No hidden columns'}
                      </Text>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </ScrollArea>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
      <AddColumnModal
        opened={addColumnModalOpened}
        onClose={() => setAddColumnModalOpened(false)}
        existingColumns={columns}
        pageKey={subPageKey || 'all'}
        onColumnAdded={onColumnAdded}
      />
    </>
  );
};

export default ManageColumnsDrawer;
